import { Box, CircularProgress, Container, Divider, Grid, Paper, Typography, useTheme } from "@mui/material";
import Main from "components/Main/Main";
import React from "react";
import SyncBar from "../../components/Common/SyncBar";
import { useIndexedDB } from "react-indexed-db-hook";
import { useAuth } from "store/AuthProvider";
import { useData } from "store/DataProvider";
import { useNavigate, useLocation } from "react-router-dom";
import { StationData } from "types/auth";
import StationDetailCard from "./components/StationDetailCard";
import { StatusCode } from "types/flagbag";
import useDataController from "hooks/useDataController";
import {
	AddCircleOutline,
	AssignmentTurnedInOutlined,
	HighlightOff,
	RemoveCircleOutline,
	TaskAlt,
	UnarchiveOutlined,
} from "@mui/icons-material";
import DehazeIcon from '@mui/icons-material/Dehaze';
import useFlagBagController from "hooks/useFlagBagController";
import { useSync } from "store/SyncContext";
import { useNetworkStatus } from "hooks/useNetworkStatus";
import { authApi } from "services/api/auth";
import { stationApi } from "services/api";
import { initIndexedDbIfMissingStore } from "utils/indexedDbRecovery";

type list = { [key: number]: { label: string; value: string | number } };

const Home = () => {
	const {
		stationOverview,
		calculateStationStats,
		getEventId,
		setStationOverviewData,
		getStationId,
	} = useData();
	const navigate = useNavigate();
	const location = useLocation();
	const { isOnline } = useNetworkStatus();

	const [isLoading, setIsLoading] = React.useState(true);
	const [stationDetail, setStationDetail] = React.useState<StationData>();
	const [total, setTotal] = React.useState(0);
	const [receivedTotal, setReceivedTotal] = React.useState(0);
	const [notReceivedTotal, setNotReceivedTotal] = React.useState(0);

	const [flagBagPreassign, setFlagBagPreassign] = React.useState(0);

	const receivedListOrder = [StatusCode.COLLECTED, StatusCode.BANK, StatusCode.COLLECTED_EMPTY, StatusCode.COLLECTED_OTHER];
	const [receivedList, setReceivedList] = React.useState<list>({});

	const unreceivedListOrder = [StatusCode.DISTRIBUTED, StatusCode.DISTRIBUTED_OTHER];
	const [unreceivedList, setUnreceivedList] = React.useState({});

	const [extra, setExtra] = React.useState(0);
	const [less, setLess] = React.useState(0);
	const [reserve, setReserve] = React.useState(0);
	const [assign, setAssign] = React.useState(0);
	const [late, setLate] = React.useState(0);
	const [loss, setLoss] = React.useState(0);

	const { isSyncing } = useSync();

	const openFlagBagList = () => {
		navigate("/bag-list", { replace: true });
	};

	const updateData = React.useCallback((data) => {
		console.log(data);
		if (!data) {
			return;
		}
		
		setTotal((data.total || 0) - (data.reserve || 0));
		setFlagBagPreassign(data.flagBagPreassign || 0);
		setReceivedTotal(data.receivedTotal || 0);
		setNotReceivedTotal(data.notReceivedTotal || 0);
		setReceivedList(data.receivedList || {});
		setUnreceivedList(data.unreceivedList || {});
		setExtra(data.extra || 0);
		setLess(data.less || 0);
		setAssign(data.assign || 0);
		setReserve((data.reserve || 0).toString());
		setLate(data.late || 0);
		setLoss(data.loss || 0);
	}, []);

	const handleRefresh = React.useCallback((data) => {
		updateData(data.updateValue);
	}, [updateData]);

	const overviewFetchInFlightRef = React.useRef(false);
	const refreshStationOverviewRef = React.useRef<() => Promise<void>>(async () => {});
	refreshStationOverviewRef.current = async () => {
		if (!isOnline || overviewFetchInFlightRef.current) {
			return;
		}

		const eventId = getEventId();
		if (!eventId) {
			return;
		}

		overviewFetchInFlightRef.current = true;
		try {
			const overview = await stationApi.getOverview(eventId);
			setStationOverviewData(overview);
		} catch (error) {
			console.error("Failed to refresh station overview:", error);
		} finally {
			overviewFetchInFlightRef.current = false;
		}
	};

	// Refresh when entering home or when connectivity returns (stable deps — no callback identity)
	React.useEffect(() => {
		if (!isOnline) {
			return;
		}

		const timeoutId = setTimeout(() => {
			void refreshStationOverviewRef.current();
		}, 1000);

		return () => clearTimeout(timeoutId);
	}, [location.pathname, isOnline]);

	// App resume (tab/app foreground): reload after 1s
	React.useEffect(() => {
		let timeoutId: ReturnType<typeof setTimeout> | null = null;

		const handleVisibilityChange = () => {
			if (document.visibilityState !== "visible") {
				if (timeoutId) {
					clearTimeout(timeoutId);
					timeoutId = null;
				}
				return;
			}

			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			timeoutId = setTimeout(() => {
				timeoutId = null;
				void refreshStationOverviewRef.current();
			}, 1000);
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);
		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		};
	}, []);

	const currentStationId = getStationId();
	
	// 统一的数据加载逻辑
	React.useEffect(() => {
		if (isSyncing) {
			return;
		}
		
		setIsLoading(true);
		
		const loadData = async () => {
			try {
				if (stationOverview?.stations && stationOverview.stations.length > 0) {
					const stations = stationOverview;
					// 根据当前选中的站点 ID 查找对应的站点数据
					const currentStation = stations.stations.find(
						s => s.station.id === currentStationId
					) || stations.stations[0]; // 如果找不到，使用第一个站点
					
					// 设置站点基本信息
					setStationDetail({
						id: currentStation.station.id,
						stationName: currentStation.station.name,
						eventName: stations.event.name,
						eventDate: stations.event.startTime,
						location: currentStation.station.location,
					});
					
					if (calculateStationStats) {
						updateData(calculateStationStats);
					}
				} /*
				// 方案2: 降级到旧的 records 数据（兼容旧数据）
				else if (currentStationId) {
					await FlagBagController.getFlagBagDetail((cachedData) => {
						if (cachedData.LastUpdateTimeLocalTime) {
							setLastUpdateDate(new Date(cachedData.LastUpdateTimeLocalTime));
						}
						updateData(cachedData);
					});
					
					// 获取站点信息
					const stationData = await FlagBagController.getStationData();
					if (stationData) {
						setStationDetail(stationData);
					}
				} */else {
					setStationDetail(undefined);
				}
			} catch (error) {
				console.error("Failed to load station data:", error);
			} finally {
				setIsLoading(false);
			}
		};
		
		loadData();
	}, [stationOverview, currentStationId, isSyncing, updateData]);

	return (
		<Main
			titleAlign="left"
			title="統計"
			titleIcon={<DehazeIcon />}
		>
			<SyncBar handleSubmit={handleRefresh} />
			<Container sx={{ mt: 3, pt: 2, pb: 10 }}>
				{isLoading ? (
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center">
						<CircularProgress color="primary" />
					</Box>
				) : (
					<>
						<StationDetailCard stationDetail={stationDetail} />
						<Grid
							container
							spacing={1}
							mb={1}>
							<Grid size={12}>
								<Box
									component={Paper}
									onClick={openFlagBagList}
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										p: 2,
										py: 1.5,
									}}>
									<Typography
										variant="h5"
										fontWeight="600">
										{`旗袋總數 ${total}(${flagBagPreassign} 未出)`}
									</Typography>
									<Typography color="primary">查看旗袋</Typography>
								</Box>
							</Grid>
							<Grid size="grow">
								<Paper sx={{ height: 1 }}>
									<Box sx={{ p: 2 }}>
										<Box display="flex" alignItems="center" justifyContent="space-between">
											<Box display="flex" alignItems="center" gap={1}>
												<TaskAlt color="primary" />
												<Typography variant="h5" fontWeight={600}>已收</Typography>
											</Box>
											<Typography textAlign="right" fontWeight={600} color="primary" variant="h5">
												{receivedTotal}
												<Box component="span" fontSize="body2.fontSize" color="text.secondary">
													/{total}
												</Box>
											</Typography>
										</Box>
										<Divider sx={{ my: 1 }} />
										<Box>
											{Object.keys(receivedList).length > 0 &&
												receivedListOrder.map((key) => {
													if (!receivedList[key]) {
														return null;
													}
													return (
														<Box
															key={key}
															display="flex"
															alignItems="center"
															justifyContent="space-between"
															gap={1}>
															<Typography>{receivedList[key].label}</Typography>
															<Typography textAlign="right">{receivedList[key].value}</Typography>
														</Box>
													);
												})}
											
										</Box>
										<Divider sx={{ my: 1 }} />
										<Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
											<Typography>遲交</Typography>
											<Typography textAlign="right">{late}</Typography>
										</Box>
										
										<Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
											<Typography>遺失</Typography>
											<Typography textAlign="right">{loss}</Typography>
										</Box>
									</Box>
								</Paper>
							</Grid>
							<Grid size="grow">
								<Paper sx={{ height: 1 }}>
									<Box sx={{ p: 2 }}>
										<Box display="flex" alignItems="center" justifyContent="space-between">
											<Box display="flex" alignItems="center" gap={1}>
												<HighlightOff color="primary" />
												<Typography variant="h5" fontWeight={600}>未收</Typography>
											</Box>
											<Typography textAlign="right" fontWeight={600} color="primary" variant="h5">
												{notReceivedTotal}
												<Box component="span" fontSize="body2.fontSize" color="text.secondary">
													/{total}
												</Box>
											</Typography>
										</Box>
										<Divider sx={{ my: 1 }} />
										<Box>
											{Object.keys(unreceivedList).length > 0 &&
												unreceivedListOrder.map((key) => {
													if (!unreceivedList[key]) {
														return null;
													}
													return (
														<Box
															key={key}
															display="flex"
															alignItems="center"
															justifyContent="space-between"
															gap={1}>
															<Typography>{unreceivedList[key].label}</Typography>
															<Typography textAlign="right">{unreceivedList[key].value}</Typography>
														</Box>
													);
												})}
										</Box>
									</Box>
								</Paper>
							</Grid>
							<Grid size={12}>
								<Paper sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2, gap: 1 }}>
									<Box
										display={"flex"}
										alignItems={"center"}
										gap={1}>
										<AssignmentTurnedInOutlined color="primary" />
										<Typography fontWeight={600}>已分配 / 未出</Typography>
									</Box>
									<Typography>{assign}</Typography>
								</Paper>
							</Grid>
							<Grid size={12}>
								<Paper sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2, gap: 1 }}>
									<Box
										display={"flex"}
										alignItems={"center"}
										gap={1}>
										<UnarchiveOutlined color="primary" />
										<Typography fontWeight={600}>後備(未分配)</Typography>
									</Box>
									<Typography>{reserve}</Typography>
								</Paper>
							</Grid>
							<Grid size={12}>
								<Paper sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2, gap: 1 }}>
									<Box
										display={"flex"}
										alignItems={"center"}
										gap={1}>
										<AddCircleOutline color="primary" />
										<Typography fontWeight={600}>
											*多收
											<Box
												component="span"
												fontWeight="normal"
												fontSize="subtitle2.fontSize"
												color="text.secondary">
												{" (來自其他旗站旗袋)"}
											</Box>
										</Typography>
									</Box>

									<Typography>{extra}</Typography>
								</Paper>
							</Grid>
							<Grid size={12}>
								<Paper sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2, gap: 1 }}>
									<Box
										display={"flex"}
										alignItems={"center"}
										gap={1}>
										<RemoveCircleOutline color="primary" />
										<Typography fontWeight={600}>
											*少收
											<Box
												component="span"
												fontWeight="normal"
												fontSize="subtitle2.fontSize"
												color="text.secondary">
												{" (其他旗站代收旗袋)"}
											</Box>
										</Typography>
									</Box>
									<Typography>{less}</Typography>
								</Paper>
							</Grid>
						</Grid>
					</>
				)}
			</Container>
		</Main>
	);
};

export default React.memo(Home);
